import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import Card from '../Card';
import Button from '../Button';
import TextButton from '../TextButton';
import Text from '../Text';
import Heading from '../Heading';
import Proportion from '../Proportion';
import Tooltip from '../Tooltip';
import MediaOverlay from '../MediaOverlay';
import { DATA_HOOKS } from './CardGalleryItem.constants';
import { classes, st } from './CardGalleryItem.st.css';
import { DragHandle } from '../DragHandle/DragHandle';
import Image from '../Image';
import { DraggableContainer } from '../DraggableContainer/DraggableContainer';
import { CardGalleryItemProps } from './CardGalleryItem.types';

class CardGalleryItem extends PureComponent<CardGalleryItemProps> {
  static displayName = 'CardGalleryItem';
  static propTypes = {
    badge: PropTypes.node,
    size: PropTypes.oneOf(['small', 'medium']),
    title: PropTypes.node,
    subtitle: PropTypes.node,
    suffix: PropTypes.node,
    draggable: PropTypes.bool,
    droppable: PropTypes.bool,
    dragHandleProps: PropTypes.object,
    dragging: PropTypes.bool,
    dragDisabled: PropTypes.bool,
    tabIndex: PropTypes.number,
    backgroundImageUrl: PropTypes.string,
    backgroundImageNode: PropTypes.node,
    primaryActionProps: PropTypes.shape({
      label: PropTypes.node,
      onClick: PropTypes.func,
      disabled: PropTypes.bool,
      disabledMessage: PropTypes.string,
    }),
    secondaryActionProps: PropTypes.shape({
      label: PropTypes.node,
      onClick: PropTypes.func,
    }),
    settingsMenu: PropTypes.node,
    dataHook: PropTypes.string,
    aspectRatio: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    imagePlacement: PropTypes.oneOf(['top', 'side']),
    showSuffixOnHover: PropTypes.bool,
    skin: PropTypes.oneOf(['standard', 'outlined']),
    skinVisibility: PropTypes.oneOf(['always', 'hover']),
    footer: PropTypes.node,
  };

  static defaultProps = {
    aspectRatio: 1.6,
    size: 'medium',
    imagePlacement: 'top',
    showSuffixOnHover: false,
    skin: 'standard',
    skinVisibility: 'always',
  };

  state = {
    isHovered: false,
    isFocused: false,
  };

  _onMouseEnter = () => {
    this.setState({ isHovered: true });
  };

  _onMouseLeave = () => {
    this.setState({ isHovered: false });
  };

  _onFocus: React.FocusEventHandler<HTMLDivElement> = e => {
    try {
      if (
        process.env.NODE_ENV === 'test' ||
        e.target.matches(':focus-visible')
      ) {
        this.setState({ isFocused: true });
      }
    } catch {
      this.setState({ isFocused: true });
    }
  };

  _onBlur = () => {
    this.setState({ isFocused: false });
  };

  _hasFooter() {
    const { title, subtitle, suffix, imagePlacement } = this.props;
    return !!(title || subtitle || suffix || imagePlacement === 'side');
  }

  _renderBadge() {
    return (
      <div className={classes.badgeWrapper} data-hook={DATA_HOOKS.Badge}>
        {this.props.badge}
      </div>
    );
  }

  _renderDragHandle() {
    const { dragHandleProps, dragging, dragDisabled } = this.props;

    return (
      <DragHandle
        {...dragHandleProps}
        dragging={dragging}
        disabled={dragDisabled}
        dataHook={DATA_HOOKS.dragHandle}
      />
    );
  }

  _renderFooter() {
    const {
      title,
      subtitle,
      size,
      suffix,
      draggable,
      imagePlacement,
      backgroundImageNode,
      backgroundImageUrl,
      settingsMenu,
    } = this.props;

    const heading =
      size === 'medium' && imagePlacement !== 'side' ? (
        <Heading
          className={classes.titleHeading}
          size="small"
          ellipsis
          dataHook={DATA_HOOKS.Title}
        >
          {title}
        </Heading>
      ) : (
        <Text
          className={classes.titleText}
          size="small"
          weight="normal"
          ellipsis
          dataHook={DATA_HOOKS.Title}
        >
          {title}
        </Text>
      );

    const textSize =
      imagePlacement === 'side' ? 'tiny' : size === 'medium' ? 'small' : 'tiny';

    return (
      <>
        <div className={classes.footer} data-hook={DATA_HOOKS.footer}>
          {draggable && (
            <div className={classes.dragHandleWrapper}>
              {this._renderDragHandle()}
            </div>
          )}
          {imagePlacement === 'side' && (
            <div
              className={st(classes.footerImage, { size })}
              data-hook={DATA_HOOKS.footerImage}
            >
              {backgroundImageUrl ? (
                <Image src={backgroundImageUrl} />
              ) : (
                backgroundImageNode
              )}
            </div>
          )}
          <div className={classes.footerContent}>
            {heading}
            <Text
              size={textSize}
              secondary
              ellipsis
              dataHook={DATA_HOOKS.Subtitle}
            >
              {subtitle}
            </Text>
          </div>
          {suffix && (
            <div className={classes.footerSuffix} data-hook={DATA_HOOKS.suffix}>
              {suffix}
            </div>
          )}
          {imagePlacement === 'side' &&
            settingsMenu &&
            this._renderSettingsMenu()}
        </div>
      </>
    );
  }

  _renderActions() {
    const { primaryActionProps, secondaryActionProps, draggable } = this.props;
    const { label, disabled, disabledMessage } = primaryActionProps!;
    const primaryAction = (
      <Button
        dataHook={DATA_HOOKS.PrimaryAction}
        disabled={disabled}
        skin="light"
        onPointerDown={draggable ? e => e.stopPropagation() : undefined}
      >
        {label}
      </Button>
    );

    return (
      <div
        className={classes.primaryAction}
        data-hook={DATA_HOOKS.HoverContent}
      >
        {disabled && disabledMessage ? (
          <Tooltip disabled={!disabled} content={disabledMessage}>
            {primaryAction}
          </Tooltip>
        ) : (
          primaryAction
        )}

        {secondaryActionProps && (
          <div className={classes.secondaryAction}>
            <TextButton
              skin="light"
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                if (secondaryActionProps.onClick) {
                  secondaryActionProps.onClick(event);
                }
                event.stopPropagation();
              }}
              dataHook={DATA_HOOKS.SecondaryAction}
              onPointerDown={
                draggable
                  ? (e: React.MouseEvent<HTMLButtonElement>) =>
                      e.stopPropagation()
                  : undefined
              }
            >
              {secondaryActionProps.label}
            </TextButton>
          </div>
        )}
      </div>
    );
  }

  _renderSettingsMenu() {
    return (
      <div className={classes.settingsMenu} data-hook={DATA_HOOKS.SettingsMenu}>
        {this.props.settingsMenu}
      </div>
    );
  }

  render() {
    const {
      primaryActionProps,
      dataHook,
      badge,
      backgroundImageUrl,
      backgroundImageNode,
      settingsMenu,
      size,
      draggable,
      dragging,
      droppable,
      dragDisabled,
      tabIndex,
      aspectRatio,
      imagePlacement,
      showSuffixOnHover,
      suffix,
      skin,
      skinVisibility,
      footer,
    } = this.props;

    const { isHovered } = this.state;
    const withCustomFooter = !!footer && imagePlacement === 'top';
    const withFooter = !!this._hasFooter();
    const isInteractive = !!primaryActionProps;
    const showActions = !dragging && !droppable && isInteractive;
    const withFloatingDragHandle = !!draggable && !withFooter;
    const withHover = showActions || withFloatingDragHandle;
    const showShadow =
      skin === 'standard' && (skinVisibility === 'always' || isHovered);

    return (
      <DraggableContainer
        draggable={draggable && !showActions}
        dragging={dragging}
        dim
        dragDisabled={dragDisabled}
        tabIndex={tabIndex}
        className={st(classes.root, {
          withFooter,
          size,
          dragging,
          dragDisabled,
          draggable,
          showActions,
          imagePlacement,
          showSuffixOnHover,
          suffix: !!suffix,
        })}
        data-hook={dataHook}
        onFocus={this._onFocus}
        onBlur={this._onBlur}
      >
        <div
          className={classes.container}
          onMouseEnter={this._onMouseEnter}
          onMouseLeave={this._onMouseLeave}
          onClick={primaryActionProps?.onClick}
          data-hook={DATA_HOOKS.Container}
        >
          <Card
            stretchVertically
            showShadow={showShadow}
            dataHook={DATA_HOOKS.Card}
            className={st(classes.cardContainer, {
              skin,
              skinVisibility,
              isInteractive,
            })}
          >
            {imagePlacement === 'top' && (
              <Proportion aspectRatio={aspectRatio}>
                <MediaOverlay
                  media={backgroundImageUrl || backgroundImageNode || ''}
                  className={classes.overlay}
                  hoverSkin={withHover ? 'dark' : undefined}
                  hovered={this.state.isHovered || this.state.isFocused}
                  dataHook={DATA_HOOKS.HoverComponent}
                >
                  {showActions && (
                    <MediaOverlay.Content visible="hover">
                      {this._renderActions()}
                    </MediaOverlay.Content>
                  )}
                  {settingsMenu && showActions && (
                    <MediaOverlay.Content visible="hover" placement="top-end">
                      {this._renderSettingsMenu()}
                    </MediaOverlay.Content>
                  )}
                </MediaOverlay>
                {badge && this._renderBadge()}
                <div className={classes.dragHandleFloatingWrapper}>
                  {withFloatingDragHandle && this._renderDragHandle()}
                </div>
              </Proportion>
            )}
            {withFooter && this._renderFooter()}
            {withCustomFooter && (
              <div data-hook={DATA_HOOKS.customFooter}>{footer}</div>
            )}
          </Card>
        </div>
      </DraggableContainer>
    );
  }
}

export default CardGalleryItem;
